import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import styles from '../../styles/MFADesignSystem.module.css';

const MFAVerification: React.FC = () => {
  const { verifyMFA } = useAuth();
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token.trim()) {
      setError('Please enter the verification code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await verifyMFA(token);
      navigate('/dashboard');
    } catch (error: any) {
      setError(error.message || 'MFA verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setToken(e.target.value);
    if (error) setError('');
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.header}>
          <div className={styles.logoContainer}>
            <img 
              src="/images/wallet-transparent.png" 
              alt="WorkWallet" 
              className={styles.logo}
            />
            <h1 className={styles.logoText}>WorkWallet</h1>
          </div>
          <h2 className={styles.title}>MFA Verification</h2>
          <p className={styles.subtitle}>Enter the verification code from your authenticator app</p>
        </div>

        <div className={styles.content}>
          {/* Error Message */}
          {error && (
            <div className={styles.error}>
              <svg className={styles.errorIcon} fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              {error}
            </div>
          )}

          {/* MFA Form */}
          <form onSubmit={handleSubmit}>
            <div className={styles.inputContainer}>
              <input
                type="text"
                className={styles.input}
                placeholder="••••••"
                value={token}
                onChange={handleChange}
                disabled={loading}
                maxLength={6}
                pattern="[0-9]{6}"
                required
              />
              <svg className={styles.inputIcon} fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zM12 11.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
              </svg>
            </div>

            <button
              type="submit"
              className={styles.button}
              disabled={loading || !token.trim()}
            >
              {loading ? (
                <>
                  <svg className={styles.spinner} fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                  Verifying...
                </>
              ) : (
                <>
                  Verify Code
                  <svg className={styles.buttonIcon} fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
                  </svg>
                </>
              )}
            </button>
          </form>

          {/* Instructions */}
          <div className={styles.instructions}>
            <h3 className={styles.instructionsTitle}>How to get your verification code:</h3>
            <div className={styles.instructionsStep}>
              <svg className={styles.instructionsIcon} fill="currentColor" viewBox="0 0 24 24">
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
              </svg>
              <p className={styles.instructionsText}>Open your authenticator app (Google Authenticator, Microsoft Authenticator, etc.)</p>
            </div>
            <div className={styles.instructionsStep}>
              <svg className={styles.instructionsIcon} fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
              <p className={styles.instructionsText}>Find the code for WorkWallet</p>
            </div>
            <div className={styles.instructionsStep}>
              <svg className={styles.instructionsIcon} fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 11H7v6h2v-6zm4 0h-2v6h2v-6zm4 0h-2v6h2v-6z"/>
              </svg>
              <p className={styles.instructionsText}>Enter the 6-digit code above</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MFAVerification;
